
import { useState } from 'react';
import { ChevronLeft } from '@/design-system/icons';
import { useTranslation } from 'react-i18next';
import type { UpdateCompanyError } from './api';
import { useCompanyAdminTabs, CompanyAdminTabs, StammdatenForm, MitarbeiterTab, DevicesTab } from './components';
import { useCompany, useUpdateCompany } from './hooks';
import { type CompanyFormData, formDataToCompany, companyToFormData } from './models';
import { useApiError } from '@/utils/errorOrData';
import { ErrorBanner } from '@/components';
import { useCountries } from '@/features/country/hooks/useCountries';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { cn } from '@/lib';
import { scrollStyleClass } from '@/utils/scrollClassHelper';

interface CompanyAdminContentViewProps {
    companyUuid: string;
    showAdminSection?: boolean;
    // When the host can show the clicked company itself (a page opening a side panel), it takes over.
    // Without it the view drills into the company in place, which is the only option inside a side panel.
    onCompanyClick?: (company: { id: string; name: string }) => void;
}

export function CompanyAdminContentView({ companyUuid, showAdminSection = false, onCompanyClick }: CompanyAdminContentViewProps) {
    const { t } = useTranslation();
    const { activeTab, setActiveTab } = useCompanyAdminTabs();
    const [saveError, setSaveError] = useState<string | null>(null);

    // Without a host handler the devices tab drills into a daughter company in place.
    // The trail records that walk, so the last entry is the company on screen
    // and the back button pops one level. Resets whenever the root companyUuid changes.
    const [trail, setTrail] = useState([companyUuid]);
    if (trail[0] !== companyUuid) {
        setTrail([companyUuid]);
    }

    // The trail always holds at least the root, the fallback only satisfies the index check.
    const viewedCompanyUuid = trail[trail.length - 1] ?? companyUuid;
    const canGoBack = trail.length > 1;

    const { data: companyLoaded, isLoading: isLoadingCompany, refetch: refetchCompany, error: companyError } = useCompany(viewedCompanyUuid);
    const { data: countriesResponse, isLoading: isLoadingCountries } = useCountries();
    const updateCompanyMutation = useUpdateCompany();

    const company = updateCompanyMutation.isPending ? updateCompanyMutation.data : companyLoaded;
    const canEditCustomerId = company?.canEditCustomerId ?? false;

    const handleSave = (formData: CompanyFormData) => {
        if (!company) return;

        setSaveError(null);
        const updatedCompany = formDataToCompany(formData, company.uuid, {
            includeCustomerId: canEditCustomerId,
        });

        updateCompanyMutation.mutate(updatedCompany, {
            onError: (error: UpdateCompanyError) => {
                setSaveError(error.message);
            },
        });
    };


    const handleParentCompanyChange = () => {
        refetchCompany();
    };

    const handleDrillIntoCompany = (target: { id: string; name: string }) => {
        if (target.id === viewedCompanyUuid) return;
        setTrail((current) => [...current, target.id]);
    };

    const handleCompanyClick = onCompanyClick ?? handleDrillIntoCompany;

    const handleBackToParent = () => {
        setTrail((current) => current.slice(0, -1));
    };


    const apiError = useApiError(companyError);


    const isLoading = isLoadingCompany || isLoadingCountries;
    const countries = countriesResponse?.countries ?? [];

    let content
    if (apiError) {
        content = (
            <ErrorBanner hasError={true}>
                {apiError.message}
            </ErrorBanner>
        )
    } else if (activeTab === 'stammdaten') {
        content = (
            <>
                {isLoading ? (
                    <div className="p-5 bg-surface border border-border shadow-sm rounded-xl">
                        <div className="animate-pulse space-y-4">
                            <div className="h-4 bg-border rounded w-1/4" />
                            <div className="h-10 bg-border rounded" />
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-10 bg-border rounded" />
                                <div className="h-10 bg-border rounded" />
                            </div>
                            <div className="h-10 bg-border rounded" />
                        </div>
                    </div>
                ) : (
                    <StammdatenForm
                        initialData={company ? companyToFormData(company) : undefined}
                        countries={countries}
                        onSave={handleSave}
                        isSaving={updateCompanyMutation.isPending}
                        error={saveError}
                        showAdminSection={showAdminSection}
                        companyId={viewedCompanyUuid}
                        parentCompanyId={company?.parentCompany?.uuid}
                        parentCompanyName={company?.parentCompany?.name}
                        canEditCustomerId={canEditCustomerId}
                        onParentCompanyChange={handleParentCompanyChange} />
                )}
            </>
        )
    } else if (activeTab === 'mitarbeiter') {
        content = (
            <MitarbeiterTab companyId={viewedCompanyUuid} />
        )
    } else if (activeTab === 'devices') {
        content = (
            <DevicesTab companyId={viewedCompanyUuid} onCompanyClick={handleCompanyClick} />
        )
    }

    return (
        <PageContentCard className={cn("space-y-4 flex-1 min-h-0 w-full min-w-0 flex flex-col", scrollStyleClass)}>
            <div className="w-full flex flex-col mx-auto min-h-0 h-full">
                {canGoBack && (
                    <header className="mb-3 flex flex-col items-start gap-y-1">
                        <button
                            type="button"
                            onClick={handleBackToParent}
                            className="inline-flex items-center gap-x-1 rounded-lg text-onsurface opacity-80 hover:bg-surface-darker focus:outline-none focus:bg-surface-darker cursor-pointer"
                            aria-label={t('common.back')}
                        >
                            <ChevronLeft className="size-4" />
                            <span>{t('common.back')}</span>
                        </button>
                        {company?.companyName && (
                            <h2 className="text-lg font-semibold text-onsurface">{company.companyName}</h2>
                        )}
                    </header>
                )}
                <CompanyAdminTabs activeTab={activeTab} onTabChange={setActiveTab} />
                {content}
            </div>
        </PageContentCard>
    );
}
